import {
  CONTENT_TYPE,
  PATH,
  DATE_TIME_FORMAT,
  excelType,
  FREE_PRICE,
  FULL_MONTH,
  HOUR_MIN,
  imageTypes,
  pdfType,
  videoType,
  wordType,
} from 'src/constants/app';
import dayjs from 'src/libs/dayjs';
import { IconProps } from 'semantic-ui-react';
import { RootState } from 'src/store';

export const formatTime = (
  time: string | undefined | null,
  options?: { format: string; defaultValue: string },
) => {
  const { defaultValue = '-', format = DATE_TIME_FORMAT } = options || {};

  if (!time) return defaultValue;
  if (dayjs(time, HOUR_MIN).isValid()) {
    return dayjs(time, HOUR_MIN).format(format);
  }

  return defaultValue;
};

export const formatPrice = (
  price: any,
  {
    currency = 'sgd',
    defaultValue = '-',
    rounded = false,
    floor = false,
  }: {
    currency?: string;
    defaultValue?: string;
    rounded?: boolean;
    floor?: boolean;
  } = {
    currency: 'sgd',
    defaultValue: '-',
    rounded: false,
    floor: false,
  },
) => {
  if (isNaN(price)) return defaultValue;

  price = Number(price / 100);
  if (rounded) {
    price = Math.round(price);
  }
  if (floor) {
    price = Math.floor(price);
  }
  return `${currency.toUpperCase()} ${price.toLocaleString()}`;
};

export const fileToBinary = (file: File) => {
  return new Promise<string | ArrayBuffer | undefined | null>((resolve) => {
    const reader = new FileReader();
    reader.onload = function (e) {
      resolve(e.target?.result);
    };
    reader.readAsBinaryString(file);
  });
};

export const getFileTypeInfo = (
  file?: Partial<File>,
): { icon: IconProps['name']; type: string | undefined; loading?: boolean } => {
  if (file?.type === 'loading') {
    return {
      icon: 'spinner',
      type: 'loading',
      loading: true,
    };
  }

  if (['FORUM', 'POLL', 'RATING'].includes(file?.type || ''))
    return {
      icon: file?.type === 'FORUM' ? 'comments outline' : 'chart bar outline',
      type: file?.type,
    };

  if (file?.type === CONTENT_TYPE.QNA) {
    return {
      icon: 'question circle outline',
      type: CONTENT_TYPE.QNA,
    };
  }

  if (imageTypes.includes(file?.type!) || file?.type === CONTENT_TYPE.IMAGE) {
    return {
      icon: 'file image outline',
      type: CONTENT_TYPE.IMAGE,
    };
  }

  if (pdfType.includes(file?.type!) || file?.type === CONTENT_TYPE.PDF) {
    return {
      icon: 'file pdf outline',
      type: CONTENT_TYPE.PDF,
    };
  }

  if (videoType.includes(file?.type!) || file?.type === CONTENT_TYPE.VIDEO) {
    return {
      icon: 'file video outline',
      type: CONTENT_TYPE.VIDEO,
    };
  }

  if (excelType.includes(file?.type!) || file?.type === 'EXCEL') {
    return {
      icon: 'file excel outline',
      type: 'EXCEL',
    };
  }

  if (wordType.includes(file?.type!) || file?.type === 'WORD') {
    return {
      icon: 'file word outline',
      type: 'WORD',
    };
  }

  return {
    icon: 'file alternate',
    type: undefined,
  };
};

export const optionsWithHomeworkIfHas = (
  class_topic: Model.ClassTopic,
  courseType = class_topic.class?.type,
) => {
  const options = class_topic.class_topic_contents.map(
    (class_topic_content) => ({
      value: class_topic_content.id,
      label: class_topic_content.file_name,
      completed:
        class_topic_content.class_topic_content_completion?.is_completed,
    }),
  );

  const moduleQuizOption = class_topic.total_questions
    ? [
        {
          label: 'Module Quiz',
          value: 'module_quiz',
          completed: class_topic.has_submitted_questions,
        },
      ]
    : [];

  const homeworkOption =
    courseType !== 'CISL' && class_topic.assignment_content
      ? [
          {
            label:
              class_topic?.class?.type === 'ACCELERATOR'
                ? 'Assignment'
                : 'Homework',
            value: 'homework',
            completed: class_topic.has_submitted_homeworks,
          },
        ]
      : [];

  return options.concat(homeworkOption, moduleQuizOption);
};

export const downloadLink = (url: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const downloadTxtUrl = async (url: string, fileName: string) => {
  const res = await fetch(url);
  const buffer = await res.arrayBuffer();

  const link = document.createElement('a');
  const blob = new Blob([buffer], { type: 'plain/text' });

  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
};

export const getExpiredDate = (
  class_user: Partial<Model.ClassUser>,
  format: string = FULL_MONTH,
) => {
  // expired date
  const { class: course, class_price_id } = class_user;
  const price = (course?.prices || []).find((p) => p.id === class_price_id);

  let expired_date: string | undefined;

  if (course?.type === 'LAUNCHPAD') {
    if (price?.price === FREE_PRICE) {
      // TODO: remove this after launchpad free
      expired_date = dayjs(class_user.created_at)
        .add(price?.limit_months, 'month')
        .format(format);
    } else {
      let extensionDays = 0;
      if (class_user.extension_days) {
        extensionDays = class_user.extension_days;
      }
      expired_date = price?.price
        ? dayjs(class_user.paid_at || class_user.created_at)
            .add(price?.limit_months, 'month')
            .add(extensionDays, 'day')
            .format(format)
        : undefined;
    }
  }

  if (
    course?.type === 'ACCELERATOR' ||
    (course?.type === 'CISL' && course.end_date)
  ) {
    expired_date = dayjs(course?.end_date).format(format);
  }

  const dateLeft = dayjs(expired_date, format).diff(dayjs(), 'day') || 0;

  return {
    expired_date,
    dateLeft,
    price,
  };
};

export const getFileEtx = (fileUrl?: string): IconProps['name'] => {
  if (!fileUrl) return undefined;

  if (fileUrl.includes('pdf')) return 'file pdf outline';

  if (
    fileUrl.includes('png') ||
    fileUrl.includes('jpeg') ||
    fileUrl.includes('jpg') ||
    fileUrl.includes('gif')
  )
    return 'file image outline';

  if (fileUrl.includes('docx') || fileUrl.includes('doc'))
    return 'file word outline';

  if (fileUrl.includes('xlsx') || fileUrl.includes('xls'))
    return 'file excel outline';

  if (fileUrl.includes('mov') || fileUrl.includes('mp4'))
    return 'file video outline';

  return 'file alternate';
};

export const getFileExtension = (fileUrl?: string) => {
  if (!fileUrl) return '';

  if (fileUrl.includes('pdf')) return pdfType[0];

  if (
    fileUrl.includes('png') ||
    fileUrl.includes('jpeg') ||
    fileUrl.includes('jpg') ||
    fileUrl.includes('gif')
  )
    return imageTypes[0];

  if (fileUrl.includes('docx') || fileUrl.includes('doc')) return wordType[0];

  if (fileUrl.includes('xlsx') || fileUrl.includes('xls')) return excelType[0];

  if (fileUrl.includes('mov') || fileUrl.includes('mp4')) return videoType[0];

  return '';
};

export const isAdminSelector = (state: RootState) =>
  state.ui.app.me?.roles.map((r) => r.name).includes('Admin');

// A teacher who is not an admin: their courses and the live Q&A live in the
// teacher console (jeno-admin), which the API scopes to what they teach.
export const isScopedTeacherSelector = (state: RootState) => {
  const names = state.ui.app.me?.roles.map((r) => r.name) || [];
  return names.includes('Teacher') && !names.includes('Admin');
};

// Admins and teachers moderate the Q&A: they may remove anyone's question.
export const isStaffSelector = (state: RootState) => {
  const names = state.ui.app.me?.roles.map((r) => r.name) || [];
  return names.includes('Teacher') || names.includes('Admin');
};

export const isUnavailableCoursePath = (
  type: Model.Class['type'] | undefined,
  pathname: string,
) => {
  const [, root, , section, content] = pathname.split('/');
  if (root !== PATH.CLASS) return false;
  if (section === 'programme-bios') return !!type && type !== 'CISL';
  if (type !== 'CISL') return false;
  return (
    ['homework', 'notes', 'one_pager', 'demo_day', 'what-next'].includes(
      section,
    ) || content === 'homework'
  );
};
